/**
 * @module classroomDuck
 * @description Redux duck for classroom state management.
 */
import { createSelector, current, PayloadAction } from "@reduxjs/toolkit";
import { createCachedSelector } from 're-reselect';
import toolIndex from "toolConstants";
import { isEmpty, filter } from "lodash";
import { getActiveTab, getAreaProperty2 } from "reduxModules/selectors/containerSelectors";

// Types for classroom-specific data structures
interface Tables {
  shape?: string;
  rows?: number;
  tablesPerRow?: number;
  totalTables?: number;
  chairsPerTable?: number;
  ghostChairs?: boolean[];
  [key: string]: any;
}

interface Instructor {
  shape?: string;
  [key: string]: any;
}

interface ClassroomArea {
  tables?: Tables;
  instructor?: Instructor;
  recalculate?: boolean;
  [key: string]: any;
}

// Define a basic state type for the classroom reducers
interface ClassroomState {
  areas: { [key: string]: ClassroomArea };
  activeTab: string;
  currentTool: string;
}

const classroomSlice = {
  reducers: {
    /**
     * Sets the tables configuration for the classroom
     * @param state - The current state.
     * @param action - The action object with tables configuration.
     */
    setTables: (state: ClassroomState, action: PayloadAction<Partial<Tables>>) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      
      const currentTables = area.tables || {};
      let defaultTables = toolIndex[state.currentTool].constants.tables[action.payload.shape]?.default ?? {};

      if (!isEmpty(defaultTables)) {
        defaultTables = {
          ...defaultTables, 
          rows: 2, 
          tablesPerRow: 4, 
          totalTables: 8
        }; // reset stuff
      }
      
      const newTables = { ...action.payload, ...defaultTables };
      area.tables = { ...currentTables, ...newTables };
      
      // Mark area for recalculation
      area.recalculate = true;
    },

    /**
     * Sets the instructor configuration for the classroom
     * @param state - The current state.
     * @param action - The action object with instructor configuration.
     */
    setInstructor: (state: ClassroomState, action: PayloadAction<Partial<Instructor>>) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      
      const currentInstructor = area.instructor || {};
      const defaultTable = toolIndex[state.currentTool].constants.instructor[action.payload.shape]?.default ?? {};
      const newTable = { ...action.payload, ...defaultTable };
      
      area.instructor = { ...currentInstructor, ...newTable };
      
      // Mark area for recalculation
      area.recalculate = true;
    }

    // todo: can this be one setter in room? beware of the 's'

    // setAreaOrientation: (state, action) => {
    //   state.areas[state.activeTab].areaOrientation = action.payload
    // },

    // setCeilingTiles: (state, action) => {
    //   state.areas[state.activeTab].ceilingTiles = { ...state.areas[state.activeTab].ceilingTiles, ...action.payload }
    // },

    // setTable: (state, action) => {
    //   const currentTable = state.areas[state.activeTab].table;
    //   const defaultTable = toolIndex[state.currentTool].constants.tables[action.payload.shape]?.default ?? {};
    //   const newTable = { ...action.payload, ...defaultTable };
    //   if (currentTable.shape === 'round') newTable.width = newTable.length;
    //   state.areas[state.activeTab].table = { ...currentTable, ...newTable }
    // },

    // setMicrophones: (state, action) => {
    //   const currentMics = state.areas[state.activeTab].microphones;
    //   const newMic = { ...action.payload };
    //   if ('model' in newMic) {
    //     newMic.height = toolIndex[state.currentTool].constants.equipment.microphones[newMic.model.replace('mic', '_mic')].height(state);
    //     newMic.coverage = ['ceilingmic', 'pendantmic'].includes(newMic.model) ?
    //       state.areas[state.activeTab].microphones.coverage ?? 'focused' : null;
    //   };
    //   state.areas[state.activeTab].microphones = { ...currentMics, ...newMic }
    //   // todo: Setting mic to videobar set camera to false
    //   // todo: audio/videobar change family to Parle
    // },

    // setAcoustics: (state, action) => {
    //   state.areas[state.activeTab].acoustics = action.payload
    // },

    // setSpeakers: (state, action) => {
    //   const currentSpkrs = state.areas[state.activeTab].speakers;
    //   const newSpeaker = { ...action.payload };
    //   state.areas[state.activeTab].speakers = { ...currentSpkrs, ...newSpeaker }
    // },

    // setCameras: (state, action) => {
    //   const currentCameras= state.areas[state.activeTab].cameras;
    //   const newCamera = { ...action.payload };
    //   state.areas[state.activeTab].cameras = { ...currentCameras, ...newCamera }
    // },

    // setProductFamily: (state, action) => {
    //   state.areas[state.activeTab].productFamily = action.payload
    // },
  },
}

/**
 * Selector to get the current classroom tables configuration
 * @param state - The Redux state
 * @returns The classroom tables configuration
 */
export const getTables = createSelector(
  [(state: any) => getAreaProperty2(state, 'tables')],
  (tables?: Tables) => tables || {}
);

/**
 * Selector to get the current instructor configuration
 * @param state - The Redux state
 * @returns The instructor configuration
 */
export const getInstructor = createSelector(
  [(state: any) => getAreaProperty2(state, 'instructor')],
  (instructor?: Instructor) => instructor || {}
);

/**
 * Selector to get the total number of students
 * @param state - The Redux state
 * @returns The total number of students
 */
export const getTotalStudents = createSelector(
  [(state: any) => getAreaProperty2(state, 'tables')],
  (tables?: Tables) => {
    if (!tables) return 0;
    
    const { totalTables, chairsPerTable, ghostChairs = [] } = tables;
    
    if (!totalTables || !chairsPerTable) return 0;
    
    // Calculate total chairs
    const totalChairs = totalTables * chairsPerTable;
    
    // Subtract ghost chairs (count the ones that are false/disabled)
    const activeGhostChairs = filter(ghostChairs, chair => chair === false).length;
    
    return totalChairs - activeGhostChairs;
  }
);

export default classroomSlice;
